import React, { useState, useEffect } from 'react';
import { ref, get, update } from 'firebase/database';
import { database } from '../firebase/config';
import { pdf } from '@react-pdf/renderer';
import BollaPDF from '../pdf/BollaPDF';
import BottoneVisualizzaBollaPDF from '../pdf/BottoneVisualizzaBollaPDF';
import ListaBollePDF from '../pdf/ListaBollePDF';
import '../styles/Liste.css';
import { formattaData } from '../utils/dateUtils';


// ─────────────────────────────────────────────
// HELPER DATA
// ─────────────────────────────────────────────
function fmtData(d) {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}


function RicercaBolle() {
  const [bolle, setBolle] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [filtri, setFiltri] = useState({
    dipendenteId: '',
    dataInizio: '',
    dataFine: '',
    cliente: '',
    cantiere: '',
    numeroBolla: '',
    fatturata: '',
    archiviata: '',
  });
  const [risultati, setRisultati] = useState([]);
  const [ricercaEffettuata, setRicercaEffettuata] = useState(false);
  const [generandoLista, setGenerandoLista] = useState(false);
  const [salvataggioPending, setSalvataggioPending] = useState({});

  useEffect(() => { caricaTuttiDati(); }, []);

  // ─────────────────────────────────────────────
  // Legge sia "bolle" (vecchie, foto) che "bolleForm" (nuove, CreaBollaForm)
  // e unifica in un array unico con campo _source per sapere da dove vengono
  // ─────────────────────────────────────────────
  async function caricaTuttiDati() {
    const [bolleSnap, bolleFormSnap, dipSnap, cantSnap, cliSnap] = await Promise.all([
      get(ref(database, 'bolle')),
      get(ref(database, 'bolleForm')),
      get(ref(database, 'dipendenti')),
      get(ref(database, 'cantieri')),
      get(ref(database, 'clienti')),
    ]);

    const dipMap  = {};
    const cantMap = {};
    const cliMap  = {};

    if (dipSnap.exists()) {
      Object.entries(dipSnap.val()).forEach(([k, v]) => { dipMap[k] = v; });
      setDipendenti(Object.entries(dipSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    }
    if (cantSnap.exists()) {
      Object.entries(cantSnap.val()).forEach(([k, v]) => { cantMap[k] = v; });
      setCantieri(Object.entries(cantSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    }
    if (cliSnap.exists()) {
      Object.entries(cliSnap.val()).forEach(([k, v]) => { cliMap[k] = v; });
      setClienti(Object.entries(cliSnap.val()).map(([k, v]) => ({ id: k, ...v })));
    }

    const tutte = [];

    // ── Bolle vecchie (upload foto) dal nodo "bolle" ──
    if (bolleSnap.exists()) {
      Object.entries(bolleSnap.val()).forEach(([k, v]) => {
        const dip = v.dipendenteId ? dipMap[v.dipendenteId] : null;
        const cant = v.cantiereId ? cantMap[v.cantiereId] : null;
        const cli  = v.clienteId  ? cliMap[v.clienteId]   : null;

        tutte.push({
          id: k,
          _source: 'bolle',
          ...v,
          // Campi risolti usati per display e filtri
          _nomeDipendente: dip
            ? `${dip.nome} ${dip.cognome}`
            : (v.nomeAutista || v.nomeDipendente || ''),
          _cantiere: cant?.luogo || v.cantiereLabel || v.cantiere || '',
          _cliente:  cli?.ragioneSociale || v.clienteLabel || v.cliente || '',
          // dipendenteId normalizzato per il filtro select
          _dipendenteId: v.dipendenteId || '',
        });
      });
    }

    // ── Bolle nuove (CreaBollaForm) dal nodo "bolleForm" ──
    if (bolleFormSnap.exists()) {
      Object.entries(bolleFormSnap.val()).forEach(([k, v]) => {
        const dip  = v.dipendenteId ? dipMap[v.dipendenteId]  : null;
        const cant = v.cantiereId   ? cantMap[v.cantiereId]    : null;
        const cli  = v.clienteId    ? cliMap[v.clienteId]      : null;

        tutte.push({
          id: k,
          _source: 'bolleForm',
          ...v,
          _nomeDipendente: dip
            ? `${dip.nome} ${dip.cognome}`
            : (v.nomeAutista || ''),
          _cantiere: cant?.luogo || v.cantiere || '',
          _cliente:  cli?.ragioneSociale || v.cliente || '',
          _dipendenteId: v.dipendenteId || '',
        });
      });
    }

    setBolle(tutte);
  }

  function handleRicerca(e) {
    e.preventDefault();
    let filtrate = [...bolle];

    if (filtri.dipendenteId)
      filtrate = filtrate.filter(b => b._dipendenteId === filtri.dipendenteId);
    if (filtri.dataInizio)
      filtrate = filtrate.filter(b => b.data >= filtri.dataInizio);
    if (filtri.dataFine)
      filtrate = filtrate.filter(b => b.data <= filtri.dataFine);
    if (filtri.cliente)
      filtrate = filtrate.filter(b =>
        b._cliente.toLowerCase().includes(filtri.cliente.toLowerCase())
      );
    if (filtri.cantiere)
      filtrate = filtrate.filter(b =>
        b._cantiere.toLowerCase().includes(filtri.cantiere.toLowerCase())
      );
    if (filtri.numeroBolla)
      filtrate = filtrate.filter(b =>
        String(b.numeroBolla || b.numero || '').includes(filtri.numeroBolla)
      );
    if (filtri.fatturata !== '')
      filtrate = filtrate.filter(b => !!b.fatturata === (filtri.fatturata === 'true'));
    if (filtri.archiviata !== '')
      filtrate = filtrate.filter(b => !!b.archiviata === (filtri.archiviata === 'true'));

    filtrate.sort((a, b) => (b.data || '').localeCompare(a.data || ''));
    setRisultati(filtrate);
    setRicercaEffettuata(true);
  }

  function resetFiltri() {
    setFiltri({ dipendenteId: '', dataInizio: '', dataFine: '', cliente: '', cantiere: '', numeroBolla: '', fatturata: '', archiviata: '' });
    setRisultati([]);
    setRicercaEffettuata(false);
  }

  // ── Salva la spunta sul nodo corretto (bolle o bolleForm) ──
  async function toggleCheck(bolla, campo) {
    const nuovoValore = !bolla[campo];
    const pendingKey = `${bolla.id}_${campo}`;
    setSalvataggioPending(prev => ({ ...prev, [pendingKey]: true }));
    try {
      // Usa il nodo Firebase corretto in base alla sorgente
      const nodo = bolla._source === 'bolleForm' ? 'bolleForm' : 'bolle';
      await update(ref(database, `${nodo}/${bolla.id}`), { [campo]: nuovoValore });
      setBolle(prev => prev.map(b => b.id === bolla.id ? { ...b, [campo]: nuovoValore } : b));
      setRisultati(prev => prev.map(b => b.id === bolla.id ? { ...b, [campo]: nuovoValore } : b));
    } catch (err) {
      alert('Errore salvataggio: ' + err.message);
    } finally {
      setSalvataggioPending(prev => {
        const next = { ...prev };
        delete next[pendingKey];
        return next;
      });
    }
  }

  function getDescrizioneFiltri() {
    const f = [];
    if (filtri.dipendenteId) {
      const d = dipendenti.find(d => d.id === filtri.dipendenteId);
      f.push(`Dipendente: ${d?.nome} ${d?.cognome}`);
    }
    if (filtri.dataInizio) f.push(`Dal: ${formattaData(filtri.dataInizio)}`);
    if (filtri.dataFine) f.push(`Al: ${formattaData(filtri.dataFine)}`);
    if (filtri.cliente) f.push(`Cliente: ${filtri.cliente}`);
    if (filtri.cantiere) f.push(`Cantiere: ${filtri.cantiere}`);
    if (filtri.numeroBolla) f.push(`N. Bolla: ${filtri.numeroBolla}`);
    if (filtri.fatturata !== '') f.push(`Fatturata: ${filtri.fatturata === 'true' ? 'Si' : 'No'}`);
    if (filtri.archiviata !== '') f.push(`Archiviata: ${filtri.archiviata === 'true' ? 'Si' : 'No'}`);
    return f.join('  |  ');
  }

  async function scaricaListaPDF() {
    setGenerandoLista(true);
    try {
      const dipendentiMap = dipendenti.reduce((acc, d) => ({ ...acc, [d.id]: d }), {});
      const blob = await pdf(
        <ListaBollePDF risultati={risultati} filtri={filtri} dipendentiMap={dipendentiMap} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ricerca-bolle-${filtri.dataInizio || 'tutte'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      alert('Errore: ' + err.message);
    } finally {
      setGenerandoLista(false);
    }
  }


  // ── Arricchisce la bolla con cantiere/cliente risolti per BottoneVisualizzaPDF ──
  function arricchisciBolla(bolla) {
    const cantiere = cantieri.find(c => c.id === bolla.cantiereId);
    const cliente  = clienti.find(c => c.id === bolla.clienteId);
    return {
      ...bolla,
      cantiere: cantiere?.luogo || bolla._cantiere || bolla.cantiereId,
      cliente:  cliente?.ragioneSociale || bolla._cliente || bolla.clienteId,
    };
  }

  const checkStyle = (attivo, pending) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `2px solid ${attivo ? '#27ae60' : '#bdc3c7'}`,
    background: attivo ? '#27ae60' : '#fff',
    color: '#fff',
    cursor: pending ? 'not-allowed' : 'pointer',
    fontSize: 16,
    fontWeight: 700,
    transition: 'all 0.15s ease',
    opacity: pending ? 0.6 : 1,
    userSelect: 'none',
  });

  return (
    <div className="lista-container">
      <div className="lista-header">
        <h2>Ricerca Bolle</h2>
      </div>

      {/* FORM FILTRI */}
      <div style={{ background: '#f8f9fa', padding: 25, borderRadius: 10, marginBottom: 30 }}>
        <form onSubmit={handleRicerca}>
          <div className="form-ricerca-programmi">

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Dipendente</label>
              <select value={filtri.dipendenteId} onChange={e => setFiltri({ ...filtri, dipendenteId: e.target.value })}>
                <option value="">-- Tutti --</option>
                {dipendenti.map(d => (
                  <option key={d.id} value={d.id}>{d.nome} {d.cognome}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Data Inizio</label>
              <input type="date" value={filtri.dataInizio} onChange={e => setFiltri({ ...filtri, dataInizio: e.target.value })} />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Data Fine</label>
              <input type="date" value={filtri.dataFine} onChange={e => setFiltri({ ...filtri, dataFine: e.target.value })} />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Cliente</label>
              <input
                type="text"
                placeholder="Cerca cliente..."
                value={filtri.cliente}
                onChange={e => setFiltri({ ...filtri, cliente: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Cantiere</label>
              <input
                type="text"
                placeholder="Cerca cantiere..."
                value={filtri.cantiere}
                onChange={e => setFiltri({ ...filtri, cantiere: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>N. Bolla</label>
              <input
                type="text"
                placeholder="Es. 42"
                value={filtri.numeroBolla}
                onChange={e => setFiltri({ ...filtri, numeroBolla: e.target.value })}
              />
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Fatturata</label>
              <select value={filtri.fatturata} onChange={e => setFiltri({ ...filtri, fatturata: e.target.value })}>
                <option value="">-- Tutte --</option>
                <option value="true">Si</option>
                <option value="false">No</option>
              </select>
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Archiviata</label>
              <select value={filtri.archiviata} onChange={e => setFiltri({ ...filtri, archiviata: e.target.value })}>
                <option value="">-- Tutte --</option>
                <option value="true">Si</option>
                <option value="false">No</option>
              </select>
            </div>

          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="submit" className="btn-add" style={{ flex: 1 }}>Cerca</button>
            <button type="button" className="btn-cancel" onClick={resetFiltri} style={{ flex: 1 }}>Reset</button>
          </div>
        </form>
      </div>

      {/* RISULTATI */}
      {!ricercaEffettuata ? (
        <div className="empty-state">
          <p>Usa i filtri sopra per cercare le bolle.</p>
          <p>Puoi filtrare per dipendente, date, cliente, cantiere, numero bolla, fatturata e archiviata.</p>
        </div>
      ) : risultati.length === 0 ? (
        <div className="empty-state">
          <p>Nessun risultato trovato con i filtri selezionati.</p>
        </div>
      ) : (
        <>
          {/* Barra gialla riepilogo */}
          <div style={{
            background: '#efd400', padding: 15, borderRadius: 8, marginBottom: 20,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 10, border: 'solid 1px #454545'
          }}>
            <div>
              <strong style={{ fontSize: 16 }}>Trovate {risultati.length} bolle</strong>
              {getDescrizioneFiltri() && (
                <p style={{ margin: '5px 0 0 0', fontSize: 13, color: '#333' }}>{getDescrizioneFiltri()}</p>
              )}
            </div>
            <button
              onClick={scaricaListaPDF}
              disabled={generandoLista}
              className="btn-add"
              style={{ background: '#2c3e50', color: '#fff', border: 'none' }}
            >
              {generandoLista ? 'Generazione...' : '📄 Scarica lista PDF'}
            </button>
          </div>

          {/* TABELLA DESKTOP */}
          <div className="desktop-only">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Dipendente</th>
                  <th>Cantiere</th>
                  <th>Cliente</th>
                  <th>N. Bolla</th>
                  <th style={{ textAlign: 'center' }}>PDF</th>
                  <th style={{ textAlign: 'center' }}>Fatturata</th>
                  <th style={{ textAlign: 'center' }}>Archiviata</th>
                </tr>
              </thead>
              <tbody>
                {risultati.map(b => {
                  const pendingFatt = !!salvataggioPending[`${b.id}_fatturata`];
                  const pendingArch = !!salvataggioPending[`${b.id}_archiviata`];
                  return (
                    <tr key={b.id}>
                      <td><strong>{formattaData(b.data)}</strong></td>
                      <td>{b._nomeDipendente || '-'}</td>
                      <td>{b._cantiere || '-'}</td>
                      <td>{b._cliente || '-'}</td>
                      <td>
                        {(b.numeroBolla || b.numero)
                          ? <span style={{ fontWeight: 700, color: '#2c3e50' }}>#{b.numeroBolla || b.numero}</span>
                          : <span style={{ color: '#bbb' }}>—</span>
                        }
                      </td>

                      {/* PDF */}
                      <td style={{ textAlign: 'center' }}>
                        {b._source === 'bolleForm'
                          ? <BottoneVisualizzaBollaPDF bolla={arricchisciBolla(b)} />
                          : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                        }
                      </td>

                      {/* Spunta FATTURATA */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          title={b.fatturata ? 'Segna come NON fatturata' : 'Segna come fatturata'}
                          disabled={pendingFatt}
                          onClick={() => toggleCheck(b, 'fatturata')}
                          style={checkStyle(b.fatturata, pendingFatt)}
                          aria-label={`Fatturata: ${b.fatturata ? 'Si' : 'No'}`}
                        >
                          {pendingFatt ? '…' : (b.fatturata ? '✓' : '')}
                        </button>
                      </td>

                      {/* Spunta ARCHIVIATA */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          title={b.archiviata ? 'Segna come NON archiviata' : 'Segna come archiviata'}
                          disabled={pendingArch}
                          onClick={() => toggleCheck(b, 'archiviata')}
                          style={checkStyle(b.archiviata, pendingArch)}
                          aria-label={`Archiviata: ${b.archiviata ? 'Si' : 'No'}`}
                        >
                          {pendingArch ? '…' : (b.archiviata ? '✓' : '')}
                        </button>
                      </td>
                    </tr>
                  );
                })}

                <tr style={{ backgroundColor: '#eaf4fb', borderTop: '2px solid #2980b9' }}>
                  <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700, padding: '8px', color: '#2980b9' }}>
                    Totale bolle:
                  </td>
                  <td colSpan={3} style={{ fontWeight: 700, fontSize: 15, color: '#2980b9', padding: '8px' }}>
                    {risultati.length}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* CARD MOBILE */}
          <div className="mobile-cards">
            {risultati.map(b => {
              const pendingFatt = !!salvataggioPending[`${b.id}_fatturata`];
              const pendingArch = !!salvataggioPending[`${b.id}_archiviata`];
              return (
                <div key={b.id} className="mobile-card">
                  <div className="mobile-card-header">
                    {formattaData(b.data)} — {b._nomeDipendente || '-'}
                    {(b.numeroBolla || b.numero) && (
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 700,
                        background: '#2c3e50', color: '#fff',
                        borderRadius: 4, padding: '2px 6px'
                      }}>
                        #{b.numeroBolla || b.numero}
                      </span>
                    )}
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Cantiere</span>
                    <span className="mobile-card-value">{b._cantiere || '-'}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Cliente</span>
                    <span className="mobile-card-value">{b._cliente || '-'}</span>
                  </div>
                  {b._source === 'bolleForm' && (
                    <div className="mobile-card-row" style={{ alignItems: 'center' }}>
                      <span className="mobile-card-label">PDF</span>
                      <BottoneVisualizzaBollaPDF bolla={arricchisciBolla(b)} />
                    </div>
                  )}
                  <div className="mobile-card-row" style={{ alignItems: 'center' }}>
                    <span className="mobile-card-label">Fatturata</span>
                    <button
                      title={b.fatturata ? 'Segna come NON fatturata' : 'Segna come fatturata'}
                      disabled={pendingFatt}
                      onClick={() => toggleCheck(b, 'fatturata')}
                      style={checkStyle(b.fatturata, pendingFatt)}
                      aria-label={`Fatturata: ${b.fatturata ? 'Si' : 'No'}`}
                    >
                      {pendingFatt ? '…' : (b.fatturata ? '✓' : '')}
                    </button>
                  </div>
                  <div className="mobile-card-row" style={{ alignItems: 'center' }}>
                    <span className="mobile-card-label">Archiviata</span>
                    <button
                      title={b.archiviata ? 'Segna come NON archiviata' : 'Segna come archiviata'}
                      disabled={pendingArch}
                      onClick={() => toggleCheck(b, 'archiviata')}
                      style={checkStyle(b.archiviata, pendingArch)}
                      aria-label={`Archiviata: ${b.archiviata ? 'Si' : 'No'}`}
                    >
                      {pendingArch ? '…' : (b.archiviata ? '✓' : '')}
                    </button>
                  </div>
                </div>
              );
            })}

            <div style={{
              background: '#eaf4fb', border: '2px solid #2980b9', borderRadius: 8,
              padding: '12px 16px', marginTop: 8,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ fontWeight: 700, color: '#2980b9', fontSize: 14 }}>Totale bolle:</span>
              <span style={{ fontWeight: 700, color: '#2980b9', fontSize: 16 }}>{risultati.length}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default RicercaBolle;
